import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/all';
gsap.registerPlugin(ScrollTrigger);
import { useEffect, useRef, useState } from 'react';

import { highlightsSlides } from '../constants';
import { pauseImg, playImg, replayImg } from '../utils';

const VideoCarousel = () => {
    const videoRef = useRef<HTMLVideoElement[] | null>([]);
    const videoSpanRef = useRef<HTMLSpanElement[] | null>([]);
    const videoDivRef = useRef<HTMLSpanElement[] | null>([]);

    // video and indicator
    const [video, setVideo] = useState({
        isEnd: false,
        startPlay: false,
        videoId: 0,
        isLastVideo: false,
        isPlaying: false,
    });

    const [loadedData, setLoadedData] = useState<
        React.SyntheticEvent<HTMLVideoElement>[]
    >([]);
    const { isEnd, isLastVideo, startPlay, videoId, isPlaying } = video;

    useGSAP(() => {
        // slider animation to move the video out of the screen and bring the next video in
        gsap.to('#slider', {
            transform: `translateX(${-100 * videoId}%)`,
            duration: 2,
            ease: 'power2.inOut', // show visualizer https://gsap.com/docs/v3/Eases
        });

        // video animation to play the video when it is in the view
        gsap.to('#video', {
            scrollTrigger: {
                trigger: '#video',
                toggleActions: 'restart none none none',
            },
            onComplete: () => {
                setVideo((pre) => ({
                    ...pre,
                    startPlay: true,
                    isPlaying: true,
                }));
            },
        });
    }, [isEnd, videoId]);

    useEffect(() => {
        let currentProgress = 0;
        const span = videoSpanRef.current;

        if (span && span[videoId]) {
            // animation to move the indicator
            const anim = gsap.to(span[videoId], {
                onUpdate: () => {
                    // get the progress of the video
                    const progress = Math.ceil(anim.progress() * 100);

                    if (progress != currentProgress) {
                        currentProgress = progress;
                        if (videoDivRef.current) {
                            // set the width of the progress bar
                            gsap.to(videoDivRef.current[videoId], {
                                width:
                                    window.innerWidth < 760
                                        ? '10vw' // mobile
                                        : window.innerWidth < 1200
                                        ? '10vw' // tablet
                                        : '4vw', // laptop
                            });
                        }

                        // set the background color of the progress bar
                        gsap.to(span[videoId], {
                            width: `${currentProgress}%`,
                            backgroundColor: 'white',
                        });
                    }
                },

                // when the video is ended, replace the progress bar with the indicator and change the background color
                onComplete: () => {
                    if (isPlaying && videoDivRef.current) {
                        gsap.to(videoDivRef.current[videoId], {
                            width: '12px',
                        });
                        gsap.to(span[videoId], {
                            backgroundColor: '#afafaf',
                        });
                    }
                },
            });

            if (videoId == 0) {
                anim.restart();
            }

            // update the progress bar
            const animUpdate = () => {
                anim.progress(
                    videoRef.current
                        ? videoRef.current[videoId]?.currentTime /
                              highlightsSlides[videoId].videoDuration
                        : 0
                );
            };

            if (isPlaying) {
                // ticker to update the progress bar
                gsap.ticker.add(animUpdate);
            } else {
                // remove the ticker when the video is paused (progress bar is stopped)
                gsap.ticker.remove(animUpdate);
            }
        }
    }, [videoId, startPlay, isPlaying]);

    useEffect(() => {
        if (loadedData.length > 3) {
            if (!isPlaying && videoRef.current) {
                videoRef.current[videoId]?.pause();
            } else {
                startPlay &&
                    videoRef.current &&
                    videoRef.current[videoId]?.play();
            }
        }
    }, [startPlay, videoId, isPlaying, loadedData]);

    // vd id is the id for every video until id becomes number 3
    const handleProcess = (type: string, i?: number) => {
        switch (type) {
            case 'video-end':
                setVideo((pre) => ({
                    ...pre,
                    isEnd: true,
                    videoId: i ? i + 1 : video.videoId + 1,
                }));
                break;

            case 'video-last':
                setVideo((pre) => ({ ...pre, isLastVideo: true }));
                break;

            case 'video-reset':
                setVideo((pre) => ({
                    ...pre,
                    videoId: 0,
                    isLastVideo: false,
                    isEnd: false,
                }));
                break;

            case 'pause':
                setVideo((pre) => ({ ...pre, isPlaying: !pre.isPlaying }));
                break;

            case 'play':
                setVideo((pre) => ({ ...pre, isPlaying: !pre.isPlaying }));
                break;

            default:
                return video;
        }
    };

    const handleLoadedMetaData = (e: React.SyntheticEvent<HTMLVideoElement>) =>
        setLoadedData((pre) => [...pre, e]);

    return (
        <>
            <div className="flex items-center">
                {highlightsSlides.map((list, i: number) => (
                    <div key={list.id} id="slider" className="sm:pr-20 pr-10">
                        <div className="video-carousel_container">
                            <div className="w-full h-full flex-center rounded-3xl overflow-hidden bg-black">
                                <video
                                    id="video"
                                    playsInline={true}
                                    className={`${
                                        list.id === 2 && 'translate-x-44'
                                    } pointer-events-none`}
                                    preload="auto"
                                    muted
                                    ref={(el) => {
                                        if (el && videoRef.current) {
                                            videoRef.current[i] = el;
                                        }
                                    }}
                                    onEnded={() =>
                                        i !== 3
                                            ? handleProcess('video-end', i)
                                            : handleProcess('video-last', i)
                                    }
                                    onPlay={() =>
                                        setVideo((pre) => ({
                                            ...pre,
                                            isPlaying: true,
                                        }))
                                    }
                                    onLoadedMetadata={(
                                        e: React.SyntheticEvent<HTMLVideoElement>
                                    ) => handleLoadedMetaData(e)}>
                                    <source src={list.video} type="video/mp4" />
                                </video>
                            </div>

                            <div className="absolute top-12 left-[5%] z-10">
                                {list.textLists.map(
                                    (text: string, i: number) => (
                                        <p
                                            key={i}
                                            className="md:text-2xl text-xl font-medium">
                                            {text}
                                        </p>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="relative flex-center mt-10">
                <div className="flex-center py-5 px-7 bg-gray-300 backdrop-blur rounded-full">
                    {videoRef.current &&
                        videoRef.current.map((_, i) => (
                            <span
                                key={i}
                                className="mx-2 w-3 h-3 bg-gray-200 rounded-full relative cursor-pointer"
                                ref={(el) => {
                                    if (el && videoDivRef.current) {
                                        videoDivRef.current[i] = el;
                                    }
                                }}>
                                <span
                                    className="absolute h-full w-full rounded-full"
                                    ref={(el) => {
                                        if (el && videoSpanRef.current) {
                                            videoSpanRef.current[i] = el;
                                        }
                                    }}
                                />
                            </span>
                        ))}
                </div>

                <button type="button" className="control-btn">
                    <img
                        src={
                            isLastVideo
                                ? replayImg
                                : !isPlaying
                                ? playImg
                                : pauseImg
                        }
                        alt={
                            isLastVideo
                                ? 'replay'
                                : !isPlaying
                                ? 'play'
                                : 'pause'
                        }
                        onClick={
                            isLastVideo
                                ? () => handleProcess('video-reset')
                                : !isPlaying
                                ? () => handleProcess('play')
                                : () => handleProcess('pause')
                        }
                    />
                </button>
            </div>
        </>
    );
};

export default VideoCarousel;
